/**
 * useWorkTime Hook
 *
 * @module modules/workTime/application/hooks/useWorkTime
 *
 * Custom hook for managing work time status with API and Socket.IO integration.
 */

import { useCallback, useEffect } from 'react';

import { useToast } from '@/shared/components';
import { useSocketIOEvent } from '@/shared/services/socketio/hooks';
import { SocketIOEvent, SocketIONamespace } from '@/shared/services/socketio/types';
import { useAuthStore } from '@/modules/auth/application/store';

import { workTimeRepository } from '../../infrastructure/repositories';
import { useWorkTimeStore } from '../store';

import type { UserStatusEventData } from '@/shared/services/socketio/types';
import type { WorkTimeType } from '../../domain/types';
import type { User } from '@/modules/user';

// ============================================================================
// Hook
// ============================================================================

/**
 * Extract error message from various error types
 */
function getErrorMessage(err: unknown): string {
  // Check for AppException with userMessage
  if (err instanceof Error) {
    const appError = err as Error & { userMessage?: string };
    if (appError.userMessage) {
      return appError.userMessage;
    }
    return err.message;
  }
  if (typeof err === 'object' && err !== null) {
    // Handle API error responses with message property
    const errorObj = err as Record<string, unknown>;
    if (typeof errorObj['userMessage'] === 'string') {
      return errorObj['userMessage'];
    }
    if (typeof errorObj['message'] === 'string') {
      return errorObj['message'];
    }
  }
  return 'Failed to update work time';
}

export function useWorkTime() {
  const toast = useToast();
  const user = useAuthStore(state => state.user);
  const workTime = useWorkTimeStore(state => state.workTime);
  const isLoading = useWorkTimeStore(state => state.isLoading);
  const error = useWorkTimeStore(state => state.error);
  const setWorkTime = useWorkTimeStore(state => state.setWorkTime);
  const updateType = useWorkTimeStore(state => state.updateType);
  const setLoading = useWorkTimeStore(state => state.setLoading);
  const setError = useWorkTimeStore(state => state.setError);

  // ============================================================================
  // Initial Fetch
  // ============================================================================

  const deriveWorkTimeType = (userData: User | null): WorkTimeType | null => {
    if (!userData) return null;
    return (userData.status as WorkTimeType | undefined) || null;
  };

  useEffect(() => {
    async function fetchInitialStatus() {
      // Only fetch if we don't have work time yet
      if (workTime) return;
      const derivedType = deriveWorkTimeType(user);
      if (derivedType) {
        setWorkTime({
          type: derivedType,
          updatedAt: new Date(),
        });
        return;
      }

      setLoading(true);
      try {
        const result = await workTimeRepository.getWorkTime();
        setWorkTime(result);
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
        // Don't show toast on initial fetch - it might be expected if user is offline
      } finally {
        setLoading(false);
      }
    }

    fetchInitialStatus();
  }, [workTime, user, setWorkTime, setLoading, setError]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // Socket.IO Real-time Updates
  // ============================================================================

  const updateUserStatus = useAuthStore(state => state.updateUserStatus);

  const handleUserStatusUpdate = useCallback(
    (data: UserStatusEventData) => {
      // Update the work time type and auth user status from socket event
      if (data.status) {
        updateType(data.status as WorkTimeType);
        updateUserStatus(data.status);
      }
    },
    [updateType, updateUserStatus]
  );

  // Subscribe to v2 user status events (/user namespace — 'status' event)
  useSocketIOEvent<UserStatusEventData>(SocketIOEvent.UserStatus, handleUserStatusUpdate, SocketIONamespace.User);

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Toggle work time to a new status
   */
  const toggleStatus = useCallback(
    async (newStatus: WorkTimeType) => {
      setLoading(true);
      setError(null);

      // Optimistic update
      const previousType = workTime?.type;
      updateType(newStatus);

      try {
        const result = await workTimeRepository.updateWorkTime(newStatus);
        setWorkTime(result);
      } catch (err) {
        // Revert on error
        if (previousType) {
          updateType(previousType);
        }
        const message = getErrorMessage(err);
        setError(message);
        toast.error(message, 'Work Time Error');
      } finally {
        setLoading(false);
      }
    },
    [workTime?.type, setWorkTime, updateType, setLoading, setError, toast]
  );

  /**
   * Start working (from online state)
   */
  const startWorking = useCallback(() => {
    return toggleStatus('working');
  }, [toggleStatus]);

  /**
   * Pause work (from working state)
   */
  const pauseWork = useCallback(() => {
    return toggleStatus('paused');
  }, [toggleStatus]);

  /**
   * Resume work (from pause state)
   */
  const resumeWork = useCallback(() => {
    return toggleStatus('working');
  }, [toggleStatus]);

  /**
   * Stop working (go back to online state)
   */
  const stopWork = useCallback(() => {
    return toggleStatus('online');
  }, [toggleStatus]);

  return {
    // State
    workTime,
    workTimeType: workTime?.type ?? 'offline',
    isLoading,
    error,

    // Actions
    toggleStatus,
    startWorking,
    pauseWork,
    resumeWork,
    stopWork,
  };
}
